import clsx from 'clsx';
import React, { useContext, useEffect, useState } from 'react';
import { SelectedAccountContext } from 'src/app/app/(user-pages)/(accounts)/account/[accountId]/AccountClientLayout';
import { InfoCircle } from 'src/app/app/icons/InfoCircle';
import { useUpdateAccountMutation } from 'src/utils/react-query-hooks';
const Tooltip = React.lazy(() => import('@/components/presentational/tailwind/TooltipWrapper'));

export const AVAILABLE_LANGUAGES = [
  { label: 'All languages', emoji: '🌎', checked: false, all: true },
  { label: 'English', emoji: '🇺🇸', checked: false },
  { label: 'Spanish', emoji: '🇪🇸', checked: false },
  { label: 'German', emoji: '🇩🇪', checked: false },
  { label: 'Italian', emoji: '🇮🇹', checked: false },
  { label: 'French', emoji: '🇫🇷', checked: false },
  { label: 'Dutch', emoji: '🇳🇱', checked: false },
  { label: 'Portuguese', emoji: '🇵🇹', checked: false },
  { label: 'Polish', emoji: '🇵🇱', checked: false },
  { label: 'Greek', emoji: '🇬🇷', checked: false },
  { label: 'Romanian', emoji: '🇷🇴', checked: false },
  { label: 'Turkish', emoji: '🇹🇷', checked: false },
  { label: 'Russian', emoji: '🇷🇺', checked: false },
  { label: 'Arabic', emoji: '🇸🇦', checked: false },
  { label: 'Chinese', emoji: '🇨🇳', checked: false },
  { label: 'Japanese', emoji: '🇯🇵', checked: false },
  { label: 'Indonesian', emoji: '🇮🇩', checked: false },
  { label: 'Hindi', emoji: '🇮🇳', checked: false },
]

export const IGLanguageOptions: React.FC = React.memo(() => {

  const { account, setAccountContext } = useContext(SelectedAccountContext);
  const { mutate: mutateAccount, isLoading: isLoadingAccountUpdate } = useUpdateAccountMutation({ accountId: account?.id, hideToast: true });
  const [selectedLanguages, setSelectedLanguages] = useState<string[]>(account.targeting_options['languages']);
  useEffect(() => {
    setSelectedLanguages(account.targeting_options['languages']);
  }, [account]);

  const [checkboxes, setCheckboxes] = useState(() => {
    const updatedLanguages = AVAILABLE_LANGUAGES.map(lang => ({
      ...lang,
      checked: selectedLanguages && selectedLanguages.includes(lang.label.toLocaleLowerCase())
    }));
    return updatedLanguages;
  });

  const handleCheckboxChange = (index) => {
    const updatedCheckboxes = [...checkboxes];
    if (updatedCheckboxes[index].all) { // If "All" option is checked

      // If "All" option is checked and no other option is checked, do nothing
      if (updatedCheckboxes[index].checked === true && updatedCheckboxes.every(checkbox => checkbox.checked === false || checkbox.all === true))
        return
      updatedCheckboxes.forEach((checkbox, i) => { // Uncheck all other options
        if (!checkbox.all)
          checkbox.checked = false;
      });
      // Toggle "All" option
      updatedCheckboxes[index].checked = !updatedCheckboxes[index].checked;

    } else { // If any other option is checked
      updatedCheckboxes[index].checked = !updatedCheckboxes[index].checked;
      if (!updatedCheckboxes.some(checkbox => checkbox.checked && !checkbox.all))
        // Check "All" option if no checkbox is selected
        updatedCheckboxes[0].checked = true;
      else
        updatedCheckboxes[0].checked = false; // Uncheck "All" option
    }
    setCheckboxes(updatedCheckboxes);
    const activeLanguages = updatedCheckboxes.filter(checkbox => checkbox.checked).map(checkbox => checkbox.label.toLocaleLowerCase())

    console.log(activeLanguages)
    mutateAccount({
      targeting_options: {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore:next-line
        ...account.targeting_options,
        languages: activeLanguages,
      }
    }, {
      onSuccess: (data) => {
        if (data)
          setAccountContext(data);
      }
    });
  };

  return (
    <>
      <React.Suspense fallback={<CardHeaderTitle />}>
        <Tooltip className="absolute left-10 ml-1 max-w-[300px] shadow-xl"
          content={"Choose the languages spoken by your desired audience. Keesy uses AI to identify the language from users' bios and posts."}
          placement="auto"
          animation="duration-300">
          <CardHeaderTitle />
        </Tooltip>
      </React.Suspense>
      <div className="flex flex-wrap justify-start gap-2 mt-1 mb-2">
        {checkboxes.map((checkbox, index) => (
          <button
            key={index}
            onClick={() => handleCheckboxChange(index)}
            className={clsx(
              "relative inline-flex items-center justify-center p-0.5 my-1 mx-0.5 text-sm font-medium cursor-pointer rounded-[10px] transition-all duration-300 overflow-hidden",
              checkbox.checked
                ? 'text-neutral-700 animate-sway-instantly'
                : 'text-neutral-700 hover:text-neutral-900 bg-white hover:bg-neutral-100 transition-all',
            )}
            style={checkbox.checked ? {} : { boxShadow: '0px 0px 0px 1px rgba(225, 226, 228, 0.56)' }}
          >
            {checkbox.checked && (
              <span className="absolute animate-fade-in duration-300 inset-0 bg-gradient-to-r from-[#7549F4] to-[#FCA6E9] rounded-[10px]"></span>
            )}
            <span className="relative px-3 py-2 overflow-hidden whitespace-nowrap rounded-[8px]"
              style={checkbox.checked ? { background: 'linear-gradient(135deg, #f6f3fd 0%, #fdf7fd 100%), linear-gradient(180deg, rgb(255, 255, 255, 0.16) 0%, rgb(255, 255, 255, 0) 100%)' } : {}}>
              <span className='mr-2'>{checkbox.emoji}</span>
              {checkbox.label}
            </span>
          </button>
        ))}
      </div>
    </>
  );
});

const CardHeaderTitle = () => (
  <div className="flex items-center gap-2 mb-4">
    <svg className="w-5 h-5" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
      <path d="M10.7607 14.1667H16.739M10.7607 14.1667L9.1665 17.5M10.7607 14.1667L13.1484 9.17419C13.3408 8.77189 13.437 8.57075 13.5686 8.50718C13.6831 8.4519 13.8166 8.4519 13.931 8.50718C14.0627 8.57075 14.1589 8.77189 14.3513 9.17419L16.739 14.1667M16.739 14.1667L18.3332 17.5M1.6665 4.16667H6.6665M6.6665 4.16667H9.58317M6.6665 4.16667V2.5M9.58317 4.16667H11.6665M9.58317 4.16667C9.16972 6.63107 8.21033 8.86349 6.80446 10.737M8.33317 11.6667C7.82273 11.4373 7.30204 11.1184 6.80446 10.737M6.80446 10.737C5.67736 9.87314 4.66881 8.68886 4.1665 7.5M6.80446 10.737C5.46722 12.5191 3.72603 13.9765 1.6665 15" stroke="url(#paint0_linear_2513_3076)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M10.7607 14.1667H16.739M10.7607 14.1667L9.1665 17.5M10.7607 14.1667L13.1484 9.17419C13.3408 8.77189 13.437 8.57075 13.5686 8.50718C13.6831 8.4519 13.8166 8.4519 13.931 8.50718C14.0627 8.57075 14.1589 8.77189 14.3513 9.17419L16.739 14.1667M16.739 14.1667L18.3332 17.5M1.6665 4.16667H6.6665M6.6665 4.16667H9.58317M6.6665 4.16667V2.5M9.58317 4.16667H11.6665M9.58317 4.16667C9.16972 6.63107 8.21033 8.86349 6.80446 10.737M8.33317 11.6667C7.82273 11.4373 7.30204 11.1184 6.80446 10.737M6.80446 10.737C5.67736 9.87314 4.66881 8.68886 4.1665 7.5M6.80446 10.737C5.46722 12.5191 3.72603 13.9765 1.6665 15" stroke="url(#paint1_linear_2513_3076)" strokeOpacity="0.16" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      <defs>
        <linearGradient id="paint0_linear_2513_3076" x1="-5.94654" y1="10.2161" x2="8.5408" y2="26.3131" gradientUnits="userSpaceOnUse">
          <stop stopColor="#7549F4" />
          <stop offset="1" stopColor="#FCA6E9" />
        </linearGradient>
        <linearGradient id="paint1_linear_2513_3076" x1="9.99984" y1="2.5" x2="9.99984" y2="17.5" gradientUnits="userSpaceOnUse">
          <stop stopColor="white" />
          <stop offset="1" stopColor="white" stopOpacity="0" />
        </linearGradient>
      </defs>
    </svg>
    <div className='inline-flex flex-row items-center'>
      <div className="inline-flex items-center text-base font-medium leading-normal text-zinc-900">
        Follower Languages
        <InfoCircle className="w-5 h-5 ml-2 text-gray-400 place-self-center ignore" />
      </div>
    </div>
  </div>
)